// Profil de l'espace connecté (Propriétaire / Agence) — source de vérité : Supabase.
//
// Le DG modifie uniquement ses informations de profil AUTORISÉES :
//   - Propriétaire : nom / prénom (comptes.nom_complet + proprietaires.nom_complet),
//     téléphone (comptes.telephone + proprietaires.telephone), adresse (proprietaires.adresse).
//   - Agence : nom de l'agence (agences.nom), téléphone (agences.telephone),
//     responsable → nom du titulaire (comptes.nom_complet).
//
// AUCUN champ sensible (role, statut, actif, type_compte, auth_user_id) n'est jamais
// écrit : la protection serveur (trigger `proteger_compte`) et la RLS les bloquent.
// L'e-mail de connexion (auth.users) n'est jamais modifié ici.

import { supabase } from "@/store/shared/supabase";

type Row = Record<string, unknown>;
const str = (value: unknown): string => (typeof value === "string" ? value : "");

export interface ProfilProprietaire {
  /** Identifiant du propriétaire (= périmètre Storage). */
  proprietaireId: string;
  nomComplet: string;
  email: string;
  telephone: string;
  whatsapp: string;
  adresse: string;
  siteWeb: string;
  ifu: string;
  rccm: string;
  /** Chemin Storage du logo (`proprietaires.logo_url`), vide si aucun logo. */
  logoPath: string;
}

export interface ProfilAgence {
  /** Identifiant de l'agence (= périmètre Storage). */
  agenceId: string;
  nomAgence: string;
  responsable: string;
  email: string;
  telephone: string;
  adresse: string;
  siteWeb: string;
  ifu: string;
  rccm: string;
  /** Chemin Storage du logo (`agences.logo_url`), vide si aucun logo. */
  logoPath: string;
}

async function compteBase(compteId: string): Promise<{ nomComplet: string; email: string; telephone: string }> {
  const { data, error } = await supabase
    .from("comptes")
    .select("nom_complet, email, telephone")
    .eq("id", compteId)
    .maybeSingle();
  if (error) throw error;
  const row = (data ?? {}) as Row;
  return { nomComplet: str(row.nom_complet), email: str(row.email), telephone: str(row.telephone) };
}

export async function fetchProfilProprietaire(compteId: string): Promise<ProfilProprietaire> {
  const [compte, proprioRes] = await Promise.all([
    compteBase(compteId),
    supabase
      .from("proprietaires")
      .select("id, nom_complet, telephone, whatsapp, adresse, site_web, ifu, rccm, logo_url")
      .eq("compte_id", compteId)
      .maybeSingle(),
  ]);
  if (proprioRes.error) throw proprioRes.error;
  const proprio = (proprioRes.data ?? {}) as Row;
  return {
    proprietaireId: str(proprio.id),
    nomComplet: str(proprio.nom_complet) || compte.nomComplet,
    email: compte.email,
    telephone: str(proprio.telephone) || compte.telephone,
    whatsapp: str(proprio.whatsapp),
    adresse: str(proprio.adresse),
    siteWeb: str(proprio.site_web),
    ifu: str(proprio.ifu),
    rccm: str(proprio.rccm),
    logoPath: str(proprio.logo_url),
  };
}

export async function saveProfilProprietaire(
  compteId: string,
  input: {
    nomComplet: string;
    telephone: string;
    whatsapp: string;
    adresse: string;
    siteWeb: string;
    ifu: string;
    rccm: string;
    logoPath: string | null;
  }
): Promise<void> {
  const nomComplet = input.nomComplet.trim();
  const telephone = input.telephone.trim() || null;
  const whatsapp = input.whatsapp.trim() || null;
  const adresse = input.adresse.trim() || null;
  const siteWeb = input.siteWeb.trim() || null;
  const ifu = input.ifu.trim() || null;
  const rccm = input.rccm.trim() || null;

  const { error: compteError } = await supabase
    .from("comptes")
    .update({ nom_complet: nomComplet, telephone })
    .eq("id", compteId);
  if (compteError) throw compteError;

  const { error: proprioError } = await supabase
    .from("proprietaires")
    .update({
      nom_complet: nomComplet,
      telephone,
      whatsapp,
      adresse,
      site_web: siteWeb,
      ifu,
      rccm,
      logo_url: input.logoPath,
    })
    .eq("compte_id", compteId);
  if (proprioError) throw proprioError;
}

export async function fetchProfilAgence(compteId: string): Promise<ProfilAgence> {
  // Lecture via RPC SECURITY DEFINER : fonctionne aussi pour un employé, qui
  // n'a pas le droit de lire directement la ligne `agences` (RLS). La fonction
  // ne renvoie que l'agence du périmètre authentifié (current_agence_id()).
  const [compte, identiteRes] = await Promise.all([
    compteBase(compteId),
    supabase.rpc("identite_documentaire_agence"),
  ]);
  if (identiteRes.error) throw identiteRes.error;
  const agence = (identiteRes.data ?? {}) as Row;
  return {
    agenceId: str(agence.id),
    nomAgence: str(agence.nom) || compte.nomComplet,
    responsable: compte.nomComplet,
    email: str(agence.email) || compte.email,
    telephone: str(agence.telephone) || compte.telephone,
    adresse: str(agence.adresse),
    siteWeb: str(agence.site_web),
    ifu: str(agence.ifu),
    rccm: str(agence.rccm),
    logoPath: str(agence.logo_url),
  };
}

export async function saveProfilAgence(
  compteId: string,
  input: {
    nomAgence: string;
    telephone: string;
    responsable: string;
    adresse: string;
    siteWeb: string;
    ifu: string;
    rccm: string;
    logoPath: string | null;
  }
): Promise<void> {
  const nomAgence = input.nomAgence.trim();
  const telephone = input.telephone.trim() || null;
  const responsable = input.responsable.trim();
  const adresse = input.adresse.trim() || null;
  const siteWeb = input.siteWeb.trim() || null;
  const ifu = input.ifu.trim() || null;
  const rccm = input.rccm.trim() || null;

  const { error: agenceError } = await supabase
    .from("agences")
    .update({
      nom: nomAgence,
      telephone,
      adresse,
      site_web: siteWeb,
      ifu,
      rccm,
      logo_url: input.logoPath,
    })
    .eq("compte_id", compteId);
  if (agenceError) throw agenceError;

  if (responsable) {
    const { error: compteError } = await supabase
      .from("comptes")
      .update({ nom_complet: responsable, telephone })
      .eq("id", compteId);
    if (compteError) throw compteError;
  }
}